/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Pixelate Node
   GPU mosaic pixelation effect with precise block sizing.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const PIXELATE_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_blockSize;

void main() {
  float size = max(1.0, u_blockSize);
  vec2 pixelPos = v_uv * u_resolution;
  vec2 blockCenter = (floor(pixelPos / size) + vec2(0.5)) * size;
  vec2 uv = clamp(blockCenter / u_resolution, 0.0, 1.0);
  fragColor = texture(u_image, uv);
}
`;

export const pixelateNode: NodeDefinition = {
  type: 'filter.pixelate',
  category: 'Filter',
  label: 'Pixelate',
  description: 'Mosaics image into retro chunky pixel blocks',
  keywords: ['pixelate', 'mosaic', 'blocks', '8bit', 'retro', 'censor'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Pixelated', type: 'image' },
  ],
  params: [
    {
      id: 'size',
      name: 'Pixel Block Size',
      type: 'slider',
      defaultValue: 16,
      min: 2,
      max: 120,
      step: 1,
      unit: 'px',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: PIXELATE_SHADER,
    getUniforms: (inputs, params, dimensions) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_resolution: [dimensions.width, dimensions.height],
      u_blockSize: Number(params.size) || 16,
    }),
  }),
};
